import { useRef, useState } from 'react';
import { AtSign } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Champ texte (input OU textarea) avec support des mentions @Prénom Nom.
 *  - `multiline` : passe en <textarea> (rows configurable)
 *  - `members` : liste d'utilisateurs pouvant être mentionnés (id, first_name, last_name)
 *  - `value` / `onChange` : contrôlé par le parent
 *  - `onSubmit` : appelé sur Entrée (input) ou Ctrl+Entrée (textarea) — sauf si dropdown mention ouvert
 *  - Dropdown avec navigation ↑/↓ + Entrée pour sélectionner.
 */
export function MentionTextInput({
    value, onChange, onSubmit,
    members = [],
    placeholder,
    disabled,
    testId,
    multiline = false,
    rows = 3,
    className,
}) {
    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    const inputRef = useRef(null);

    const matches = mentionQuery === null ? [] : (members || []).filter((m) => {
        const q = mentionQuery.toLowerCase().trim();
        if (!q) return true;
        const full = `${m.first_name || ''} ${m.last_name || ''}`.trim().toLowerCase();
        return full.includes(q) || (m.first_name || '').toLowerCase().startsWith(q);
    }).slice(0, 6);

    const pick = (m) => {
        const el = inputRef.current;
        if (!el) return;
        const caret = el.selectionStart ?? value.length;
        const before = value.slice(0, caret);
        const after = value.slice(caret);
        const atIdx = before.lastIndexOf('@');
        if (atIdx < 0) return;
        const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
        const newVal = `${value.slice(0, atIdx)}@${name} ${after}`;
        onChange(newVal);
        setMentionQuery(null);
        setMentionIndex(0);
        setTimeout(() => {
            el.focus();
            const p = atIdx + name.length + 2;
            el.setSelectionRange(p, p);
        }, 0);
    };

    const onKeyDown = (e) => {
        if (mentionQuery !== null && matches.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => (i + 1) % matches.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => (i - 1 + matches.length) % matches.length); return; }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); pick(matches[mentionIndex]); return; }
            if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return; }
        }
        // Submission :
        //  - input : Entrée sans Shift
        //  - textarea : Ctrl/Cmd + Entrée (Entrée seul = nouvelle ligne)
        if (multiline) {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && mentionQuery === null) {
                e.preventDefault(); onSubmit?.();
            }
        } else {
            if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
                e.preventDefault(); onSubmit?.();
            }
        }
    };

    const onChangeInternal = (e) => {
        const v = e.target.value;
        onChange(v);
        const caret = e.target.selectionStart ?? v.length;
        const before = v.slice(0, caret);
        const atIdx = before.lastIndexOf('@');
        if (atIdx >= 0) {
            const between = before.slice(atIdx + 1);
            if (/^[A-Za-zÀ-ÖØ-öø-ÿ\- ]{0,40}$/.test(between) && between.split(' ').length <= 3) {
                setMentionQuery(between);
                setMentionIndex(0);
                return;
            }
        }
        setMentionQuery(null);
    };

    const baseClass = multiline
        ? 'w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none'
        : 'w-full text-xs border border-slate-200 rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200';

    return (
        <div className={cn('relative flex-1', multiline && 'w-full')}>
            {mentionQuery !== null && matches.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-1 border border-slate-200 rounded-md bg-white shadow-md max-h-40 overflow-y-auto z-20" data-testid={`${testId}-mention-dropdown`}>
                    <div className="text-[10px] text-slate-500 px-2 py-1 border-b bg-slate-50 flex items-center gap-1">
                        <AtSign className="w-3 h-3" /> Mentionner un membre
                    </div>
                    {matches.map((m, i) => (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => pick(m)}
                            className={cn(
                                "w-full text-left px-2 py-1.5 text-xs hover:bg-indigo-50 flex items-center gap-2",
                                i === mentionIndex && "bg-indigo-50",
                            )}
                            data-testid={`${testId}-mention-opt-${m.id}`}
                        >
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] flex items-center justify-center font-semibold shrink-0">
                                {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                            </span>
                            <span>{m.first_name} {m.last_name}</span>
                        </button>
                    ))}
                </div>
            )}
            {multiline ? (
                <textarea
                    ref={inputRef}
                    value={value}
                    onChange={onChangeInternal}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={rows}
                    className={cn(baseClass, className)}
                    data-testid={testId}
                />
            ) : (
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={onChangeInternal}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn(baseClass, className)}
                    data-testid={testId}
                />
            )}
        </div>
    );
}

/** Extrait les ids d'utilisateurs mentionnés dans un texte, à partir de la liste des membres. */
export function extractMentionIds(raw, members) {
    if (!raw || !members || members.length === 0) return [];
    const ids = [];
    for (const m of members) {
        const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
        if (!name) continue;
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`@${escaped}\\b`);
        if (re.test(raw)) ids.push(m.id);
    }
    return ids;
}

/** Convertit une liste d'IDs mentionnés en noms lisibles (Prénom Nom) — pour Linkify. */
export function mentionIdsToNames(mentionIds, members) {
    if (!mentionIds || !members) return [];
    return mentionIds
        .map((id) => members.find((m) => m.id === id))
        .filter(Boolean)
        .map((m) => `${m.first_name || ''} ${m.last_name || ''}`.trim())
        .filter(Boolean);
}
